import { LocalDate } from "@js-joda/core";
import { DateInterval, IInterval } from "./dateInterval";

/**
 * Compares 2 intervals if the start dates are different, use the order of the start dates.
 * If they are the same, use the end date result of comparing the end dates.
 * @param left - The first interval
 * @param right - The second interval
 * @returns -1 if left starts (or ends when start date is the same) before right, 0 if the intervals are identical or +1 if right comes before left
 */
function comparer(left: DateInterval, right: DateInterval): number {
    const startOrder = left.start.compareTo(right.start);
    if (startOrder === 0)
        return left.end.compareTo(right.end);

    return startOrder;
}

/**
 * Combines overlapping or abutting intervals to a single interval
 * @param intervals - The intervals to merge
 * @returns An array of combined intervals
 */
function mergeIntervals(intervals: DateInterval[]): DateInterval[] {
    if (intervals.length === 0)
        return [];

    const sorted = [...intervals].sort(comparer);

    let current: DateInterval | null = null;

    const merged: DateInterval[] = [];

    // IMPORTANT: the immutability means we can only push an interval onto the array when we know it will not "grow" any longer
    for (const interval of sorted) {
        if (current === null) {
            current = interval;
            continue;
        }

        if (shouldMerge(current, interval))
            current = current.span(interval);
        else {
            merged.push(current);
            current = interval.copy();
        }
    }

    if (current !== null)
        merged.push(current);

    return merged;

    function shouldMerge(left: DateInterval, right: DateInterval): boolean {
        return left.overlaps(right) || left.abuts(right);
    }
}

type MissingPeriod = { DateFrom: LocalDate, DateTo: LocalDate | null }

export interface DateRangeValidationResult {
    Success: boolean;
    MissingPeriods: MissingPeriod[];
}

/**
 * The DateRangeValidator uses the realisation that [DateFrom, DateTo] is the same as looking at subsets of the natural numbers [a, b]
 * It follows that requiring all dates between Start and End to have at least one entry is the same as looking at the union of the intervals.
 * A union of 2 intervals reduces to a single interval when the intervals touch or overlap.
 * The former is a consequence of the relation to the natural numbers. If we had to consider arbitrary precision times (= real numbers) this would no longer be sufficient.
 */
export class DateRangeValidator {
    private readonly targetInterval: DateInterval;
    constructor(startDate: LocalDate) {
        this.targetInterval = new DateInterval(startDate);
    }

    public IsTargetIntervalCovered(intervals: IInterval[]): DateRangeValidationResult {

        // Convert intervals to DateInterval
        const dateIntervals = intervals.map(i => DateInterval.from(i));

        // Combine the individual intervals
        const merged = mergeIntervals(dateIntervals);

        // Exclude intervals that end before target.Start or start after target.End
        const relevantIntervals = merged.filter(i => !i.end.isBefore(this.targetInterval.start) && !i.start.isAfter(this.targetInterval.end));

        // No intervals that overlap with the target interval
        if (relevantIntervals.length === 0) {
            return {
                Success: false,
                MissingPeriods: [convertTargetInterval(this.targetInterval)]
            };
        }

        // If there is more than 1 interval left after merging we know there are gaps
        if (relevantIntervals.length !== 1) {
            return {
                Success: false,
                MissingPeriods: getGapsBetweenIntervals(relevantIntervals)
            };
        }

        const interval = relevantIntervals[0];
        if (!interval.covers(this.targetInterval)) {
            return {
                Success: false,
                MissingPeriods: getMissingPeriodsForSingleInterval(interval, this.targetInterval)
            };
        }

        // When only 1 interval is left we only need to ensure that 
        return {
            Success: true,
            MissingPeriods: []
        };

        function getMissingPeriodsForSingleInterval(interval: DateInterval, targetInterval: DateInterval) {
            const isBeginningNotCovered = interval.start.isAfter(targetInterval.start);
            const isEndNotCovered = interval.end.isBefore(targetInterval.end);

            const missingPeriods: MissingPeriod[] = [];

            if (isBeginningNotCovered) {
                const start = targetInterval.start;
                const end = interval.start.minusDays(1);

                missingPeriods.push({ DateFrom: start, DateTo: end });
            }

            if (isEndNotCovered) {
                const start = interval.end.plusDays(1);

                missingPeriods.push({ DateFrom: start, DateTo: null });
            }
            return missingPeriods;
        }

        function getGapsBetweenIntervals(intervals: DateInterval[]) {
            const missingPeriods: MissingPeriod[] = [];
            for (let i = 1; i < intervals.length; i++) {
                const start = relevantIntervals[i - 1].end.plusDays(1);
                const end = relevantIntervals[i].start.minusDays(1);

                missingPeriods.push({ DateFrom: start, DateTo: end });
            }
            return missingPeriods;
        }

        function convertTargetInterval(interval: DateInterval): MissingPeriod {
            return {
                DateFrom: interval.start,
                DateTo: null
            };
        }
    }
}
